// Silenciar / reativar os avisos de UMA conversa (D2, passo 14).
//
// O servidor guarda o silêncio por membro (`PATCH /chat/rooms/{id}/notifications-muted`) e a
// LISTA `GET /chat/rooms` o devolve por sala (`notifications_muted`). A conversa silenciada
// continua contando as não lidas; só não avisa nem toca (a menção avisa pelo nível de "Menções
// no chat").
//
// ## Dois chamadores, um só cache
//
// - Dentro de componente (menu da conversa, painel do grupo): `useSilenciarConversa()`.
// - Fora de componente (o cartão do aviso, via `NotificationContext`, que tem o `queryClient`
//   mas não é um componente de tela): `silenciarConversa(qc, roomId, muted)`.
//
// Os dois pintam a lista na hora (otimista), restauram se o servidor recusar e reconferem com o
// servidor no fim. `aplicarSilencioNoCache` também é usada sozinha pelo `NotificationContext`.
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { QueryClient, QueryKey } from "@tanstack/react-query";
import chatService from "@/services/chatService";
import type { ChatRoom } from "@/types/chat";
import { isChatRoomsListKey } from "./useChat";

/**
 * O que a sala valia em cada lista ANTES do otimismo, para o erro devolver só isso.
 *
 * ⚠️ Restaura o CAMPO, não a lista inteira: enquanto o pedido está no ar chegam mensagens (o
 * contador sobe) e a lista é refeita pelo WebSocket. Devolver um instantâneo da lista inteira
 * apagaria esses avanços; devolver só `notifications_muted` não apaga nada.
 */
export type SilencioAnterior = Array<{ queryKey: QueryKey; muted: boolean | undefined }>;

function mexerNaSala(qc: QueryClient, roomId: string, mudar: (sala: ChatRoom) => ChatRoom): void {
  // Predicado da LISTA (`isChatRoomsListKey`), nunca o prefixo ["chat","rooms"]: ele casaria as
  // queries escopadas na sala aberta (mensagens, membros), cujos itens não são `ChatRoom`.
  qc.setQueriesData<ChatRoom[]>({ predicate: isChatRoomsListKey }, (lista) =>
    Array.isArray(lista) ? lista.map((sala) => (sala.id === roomId ? mudar(sala) : sala)) : lista
  );
}

/**
 * Pinta o silêncio da sala em TODAS as listas em cache (a padrão, a de arquivadas...).
 * Muda só a sala certa e só o campo `notifications_muted`; devolve o valor de antes de cada
 * lista para `restaurarSilencioNoCache`.
 */
export function aplicarSilencioNoCache(
  qc: QueryClient,
  roomId: string,
  muted: boolean
): SilencioAnterior {
  const anterior: SilencioAnterior = [];
  for (const [queryKey, lista] of qc.getQueriesData<ChatRoom[]>({
    predicate: isChatRoomsListKey,
  })) {
    const sala = Array.isArray(lista) ? lista.find((s) => s.id === roomId) : undefined;
    if (sala) anterior.push({ queryKey, muted: sala.notifications_muted });
  }
  mexerNaSala(qc, roomId, (sala) => ({ ...sala, notifications_muted: muted }));
  return anterior;
}

/** Devolve a cada lista o silêncio que a sala tinha antes de `aplicarSilencioNoCache`. */
export function restaurarSilencioNoCache(
  qc: QueryClient,
  roomId: string,
  anterior: SilencioAnterior
): void {
  for (const { queryKey, muted } of anterior) {
    qc.setQueryData<ChatRoom[]>(queryKey, (lista) =>
      Array.isArray(lista)
        ? lista.map((sala) => (sala.id === roomId ? { ...sala, notifications_muted: muted } : sala))
        : lista
    );
  }
}

/**
 * Reconfere as listas com o servidor. Sem `await` de propósito: quem silenciou quer saber se o
 * PEDIDO deu certo (é o que o cartão do aviso mostra), não esperar a lista inteira voltar.
 */
function reconferirListas(qc: QueryClient): void {
  void qc.invalidateQueries({ predicate: isChatRoomsListKey });
}

/**
 * Antes de pintar, para uma busca da lista que já estava no ar (o WebSocket refaz a lista a toda
 * mensagem): a resposta dela é de ANTES do pedido e, ao chegar, desfaria o otimismo por um
 * instante. O `reconferirListas` do fim refaz a busca cancelada.
 */
function cancelarBuscasDasListas(qc: QueryClient): Promise<void> {
  return qc.cancelQueries({ predicate: isChatRoomsListKey });
}

interface Variaveis {
  roomId: string;
  muted: boolean;
}

/**
 * 🔴 A sala vai COM cada chamada (`mutateAsync({ roomId, muted })`), não presa ao hook: quem
 * chama pode trocar de conversa antes do `await` (mesma lição do `useSendMessage`).
 *
 * 🔴 O erro NÃO é relançado no `onError`: um `throw` ali não chega ao `mutateAsync`, que rejeita
 * com o erro ORIGINAL do axios. Quem chama trata a rejeição ("Não silenciou.").
 */
export function useSilenciarConversa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roomId, muted }: Variaveis) => chatService.setNotificationsMuted(roomId, muted),
    onMutate: async ({ roomId, muted }) => {
      await cancelarBuscasDasListas(qc);
      return { anterior: aplicarSilencioNoCache(qc, roomId, muted) };
    },
    onError: (_erro, { roomId }, contexto) => {
      if (contexto) restaurarSilencioNoCache(qc, roomId, contexto.anterior);
    },
    // Sucesso ou erro: a resposta do servidor pode ter se perdido no caminho, e só a lista dele
    // diz como a sala ficou de verdade. Sem devolver a promessa (ver `reconferirListas`).
    onSettled: () => {
      reconferirListas(qc);
    },
  });
}

/**
 * O mesmo, fora de componente: aplica no cache, chama o serviço, restaura se falhar e RELANÇA o
 * erro original (o cartão do aviso mostra "Não silenciou." a partir da rejeição). Reconfere as
 * listas ao terminar, com sucesso ou não, como o hook.
 */
export async function silenciarConversa(
  qc: QueryClient,
  roomId: string,
  muted: boolean
): Promise<void> {
  await cancelarBuscasDasListas(qc);
  const anterior = aplicarSilencioNoCache(qc, roomId, muted);
  try {
    await chatService.setNotificationsMuted(roomId, muted);
  } catch (erro) {
    restaurarSilencioNoCache(qc, roomId, anterior);
    throw erro;
  } finally {
    reconferirListas(qc);
  }
}
